import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Package } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientAlerts = ({ token, alerts, onRefresh }) => {
  const [selectedAlerts, setSelectedAlerts] = useState([]);
  const [editingAlert, setEditingAlert] = useState(null);
  const [editAlertForm, setEditAlertForm] = useState({ tracking_number: '', description: '', store_origin: '', estimated_weight: '', declared_value: '', notes: '' });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };

  const handleSelectAlert = (alertId) => { setSelectedAlerts(prev => prev.includes(alertId) ? prev.filter(id => id !== alertId) : [...prev, alertId]); };
  const handleSelectAllAlerts = (e) => { e.target.checked ? setSelectedAlerts(alerts.map(a => a.id)) : setSelectedAlerts([]); };

  const confirmDeleteAlerts = async () => {
    setIsDeleting(true);
    try {
      await axios.post(`${API}/client/alerts/batch-delete`, { alert_ids: selectedAlerts }, axiosConfig);
      setShowDeleteConfirm(false); setSelectedAlerts([]); onRefresh();
      alert(`${selectedAlerts.length} alerta(s) eliminada(s) exitosamente`);
    } catch (error) { alert('Error al eliminar alertas: ' + (error.response?.data?.detail || error.message)); }
    finally { setIsDeleting(false); }
  };

  const openEditAlertModal = (alertItem) => {
    setEditingAlert(alertItem);
    setEditAlertForm({ tracking_number: alertItem.tracking_number || '', description: alertItem.description || '', store_origin: alertItem.store_origin || '', estimated_weight: alertItem.estimated_weight || '', declared_value: alertItem.declared_value || '', notes: alertItem.notes || '' });
  };

  const handleSaveAlertEdit = async () => {
    if (!editingAlert) return;
    try {
      await axios.put(`${API}/client/alerts/${editingAlert.id}`, editAlertForm, axiosConfig);
      alert('Alerta actualizada exitosamente. Recuerda que solo puedes editar una vez.');
      setEditingAlert(null); onRefresh();
    } catch (error) { alert('Error al actualizar alerta: ' + (error.response?.data?.detail || error.message)); }
  };

  return (
    <>
      <Card>
        <CardHeader><CardTitle>Mis Alertas de Paquetes ({alerts.length})</CardTitle><CardDescription>Paquetes que has notificado al warehouse</CardDescription></CardHeader>
        <CardContent>
          {selectedAlerts.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center justify-between">
              <span className="text-red-700 font-medium">{selectedAlerts.length} alerta(s) seleccionada(s)</span>
              <div className="flex gap-2">
                <Button variant="destructive" size="sm" onClick={() => setShowDeleteConfirm(true)}>Eliminar Seleccionadas</Button>
                <Button variant="outline" size="sm" onClick={() => setSelectedAlerts([])}>Cancelar</Button>
              </div>
            </div>
          )}
          {alerts.length > 0 && (
            <div className="mb-3 pb-2 border-b"><label className="flex items-center gap-2 text-sm text-gray-600"><input type="checkbox" checked={selectedAlerts.length === alerts.length && alerts.length > 0} onChange={handleSelectAllAlerts} className="rounded border-gray-300" />Seleccionar todas</label></div>
          )}
          <div className="space-y-3">
            {alerts.map(alertItem => (
              <div key={alertItem.id} className={`border p-4 rounded-lg ${selectedAlerts.includes(alertItem.id) ? 'ring-2 ring-blue-400' : alertItem.status === 'pending' ? 'bg-yellow-50 border-yellow-200' : alertItem.status === 'acknowledged' ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
                <div className="flex items-start gap-3">
                  <input type="checkbox" checked={selectedAlerts.includes(alertItem.id)} onChange={() => handleSelectAlert(alertItem.id)} className="mt-1 rounded border-gray-300" />
                  <Package className={`mt-1 ${alertItem.status === 'pending' ? 'text-yellow-600' : alertItem.status === 'acknowledged' ? 'text-blue-600' : 'text-green-600'}`} size={20} />
                  <div className="flex-grow">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-semibold text-lg">{alertItem.tracking_number}</h4>
                        <p className="text-sm font-medium text-gray-700 mt-1">{alertItem.description}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${alertItem.status === 'pending' ? 'bg-yellow-200 text-yellow-800' : alertItem.status === 'acknowledged' ? 'bg-blue-200 text-blue-800' : 'bg-green-200 text-green-800'}`}>
                          {alertItem.status === 'pending' ? 'Pendiente' : alertItem.status === 'acknowledged' ? 'Reconocido' : 'Procesado'}
                        </span>
                        {!alertItem.has_been_edited && alertItem.status !== 'processed' && <Button variant="outline" size="sm" onClick={() => openEditAlertModal(alertItem)}>Editar</Button>}
                        {alertItem.has_been_edited && <span className="text-xs text-gray-400 italic">Ya editada</span>}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
                      {alertItem.store_origin && <p className="text-gray-600"><strong>Tienda:</strong> {alertItem.store_origin}</p>}
                      {alertItem.estimated_weight && <p className="text-gray-600"><strong>Peso est.:</strong> {alertItem.estimated_weight} lbs</p>}
                      {alertItem.declared_value && <p className="text-gray-600"><strong>Valor:</strong> ${alertItem.declared_value}</p>}
                    </div>
                    {alertItem.notes && <p className="text-sm text-gray-500 mt-2 italic"><strong>Notas:</strong> {alertItem.notes}</p>}
                    <p className="text-xs text-gray-400 mt-2">Notificado: {new Date(alertItem.created_at).toLocaleString()}</p>
                    {alertItem.acknowledged_at && <p className="text-xs text-green-600 mt-1">Reconocido por el warehouse: {new Date(alertItem.acknowledged_at).toLocaleString()}</p>}
                  </div>
                </div>
              </div>
            ))}
            {alerts.length === 0 && (
              <div className="text-center py-12"><Package className="mx-auto h-12 w-12 text-gray-400" /><p className="text-gray-500 mt-2">No has notificado ningun paquete</p><p className="text-xs text-gray-400 mt-1">Usa el formulario para avisar sobre paquetes que te van a llegar</p></div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Edit Alert Modal */}
      {editingAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4"><h2 className="text-xl font-bold">Editar Alerta</h2><button onClick={() => setEditingAlert(null)} className="text-gray-500 hover:text-gray-700 text-2xl">x</button></div>
            <div className="bg-yellow-50 border border-yellow-200 p-3 rounded mb-4"><p className="text-sm text-yellow-800"><strong>Importante:</strong> Solo puedes editar esta alerta una vez.</p></div>
            <div className="space-y-4">
              <div><Label>Numero de Tracking</Label><Input value={editAlertForm.tracking_number} onChange={(e) => setEditAlertForm({...editAlertForm, tracking_number: e.target.value})} className="mt-1" /></div>
              <div><Label>Descripcion</Label><Input value={editAlertForm.description} onChange={(e) => setEditAlertForm({...editAlertForm, description: e.target.value})} className="mt-1" /></div>
              <div><Label>Tienda/Origen</Label><Input value={editAlertForm.store_origin} onChange={(e) => setEditAlertForm({...editAlertForm, store_origin: e.target.value})} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Peso Estimado (lbs)</Label><Input type="number" step="0.1" value={editAlertForm.estimated_weight} onChange={(e) => setEditAlertForm({...editAlertForm, estimated_weight: e.target.value})} className="mt-1" /></div>
                <div><Label>Valor Declarado ($)</Label><Input type="number" step="0.01" value={editAlertForm.declared_value} onChange={(e) => setEditAlertForm({...editAlertForm, declared_value: e.target.value})} className="mt-1" /></div>
              </div>
              <div><Label>Notas</Label><Textarea value={editAlertForm.notes} onChange={(e) => setEditAlertForm({...editAlertForm, notes: e.target.value})} className="mt-1" rows={3} /></div>
            </div>
            <div className="flex justify-end gap-2 mt-6"><Button variant="outline" onClick={() => setEditingAlert(null)}>Cancelar</Button><Button onClick={handleSaveAlertEdit}>Guardar Cambios</Button></div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="text-center">
              <h2 className="text-xl font-bold mb-2">Confirmar Eliminacion</h2>
              <p className="text-gray-600 mb-6">Esta seguro de eliminar <strong>{selectedAlerts.length}</strong> alerta(s)?<br /><span className="text-red-600 text-sm">Esta accion no se puede deshacer.</span></p>
              <div className="flex justify-center gap-3">
                <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting}>Cancelar</Button>
                <Button variant="destructive" onClick={confirmDeleteAlerts} disabled={isDeleting}>{isDeleting ? 'Eliminando...' : 'Si, Eliminar'}</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Create Alert
export const ClientCreateAlert = ({ token, alertForm, setAlertForm, onSubmit }) => (
  <Card>
    <CardHeader><CardTitle>Notificar Paquete Entrante</CardTitle><CardDescription>Avisa al master sobre paquetes que te van a llegar</CardDescription></CardHeader>
    <CardContent>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-2"><Label>Numero de Tracking *</Label><Input value={alertForm.tracking_number} onChange={(e) => setAlertForm({...alertForm, tracking_number: e.target.value})} placeholder="Ej: 1Z999AA10123456784" required /><p className="text-xs text-gray-500">Numero de rastreo del paquete (USPS, FedEx, UPS, etc.)</p></div>
        <div className="space-y-2"><Label>Descripcion del Contenido *</Label><Textarea value={alertForm.description} onChange={(e) => setAlertForm({...alertForm, description: e.target.value})} placeholder="Ej: MacBook Pro 16, iPhone 15 Pro, Ropa deportiva..." rows={3} required /></div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2"><Label>Tienda/Origen</Label><Input value={alertForm.store_origin} onChange={(e) => setAlertForm({...alertForm, store_origin: e.target.value})} placeholder="Ej: Amazon, eBay, Walmart..." /></div>
          <div className="space-y-2"><Label>Peso Estimado (lbs)</Label><Input type="number" step="0.01" value={alertForm.estimated_weight} onChange={(e) => setAlertForm({...alertForm, estimated_weight: e.target.value})} placeholder="0.00" /></div>
        </div>
        <div className="space-y-2"><Label>Valor Declarado (USD)</Label><Input type="number" step="0.01" value={alertForm.declared_value} onChange={(e) => setAlertForm({...alertForm, declared_value: e.target.value})} placeholder="0.00" /><p className="text-xs text-gray-500">Valor del producto para fines de declaracion aduanera</p></div>
        <div className="space-y-2"><Label>Notas Adicionales</Label><Textarea value={alertForm.notes} onChange={(e) => setAlertForm({...alertForm, notes: e.target.value})} placeholder="Instrucciones especiales, fragilidad, etc..." rows={3} /></div>
        <div className="bg-blue-50 border border-blue-200 p-3 rounded"><p className="text-sm text-blue-800"><strong>Importante:</strong> Notifica tus paquetes antes de que lleguen al warehouse para un mejor seguimiento.</p></div>
        <Button type="submit" className="w-full"><Package className="mr-2" size={18} />Notificar Paquete</Button>
      </form>
    </CardContent>
  </Card>
);

export default ClientAlerts;
